import DecodeJSON from '@plugins/Editor/functions/DecodeJSON'
import EncodeJSON from '@plugins/Editor/functions/EncodeJSON'
import getInteractionJSONFromNode from '@plugins/Editor/functions/getInteractionJSONFromNode'

export type GeneratedImageEntry = {
  image_url: string
  mime_type: string
}

export type MergedHtmlImageSlot = {
  interactionElement: HTMLElement
  jsonDiv: HTMLElement
  decoded: Record<string, unknown>
  prompt: string
}

/**
 * Depth-first pre-order walk so interaction blocks are collected in document order.
 */
function walkElementsPreOrder(
  element: Element,
  visit: (el: Element) => void
): void {
  visit(element)
  for (let i = 0; i < element.children.length; i += 1) {
    walkElementsPreOrder(element.children[i] as Element, visit)
  }
}

/**
 * Parses merged HTML, visits elements in tree order, finds each `div[data-interaction="true"]`,
 * decodes the embedded interaction JSON, and collects slots whose payload includes a string `image`
 * (prompt, data URL, or path) in document order — index aligns with `generated_images` from the backend.
 */
export function extractImagePromptSlotsFromMergedHtml(
  mergedHtml: string
): { rootDocument: Document; slots: MergedHtmlImageSlot[] } {
  const parser = new DOMParser()
  const rootDocument = parser.parseFromString(mergedHtml, 'text/html')
  const body = rootDocument.body
  const slots: MergedHtmlImageSlot[] = []

  walkElementsPreOrder(body, (el) => {
    if (el.tagName.toLowerCase() !== 'div') return
    if (el.getAttribute('data-interaction') !== 'true') return

    const jsonDiv = el.querySelector('div')
    if (!jsonDiv || !(jsonDiv instanceof HTMLElement)) return

    const decoded = getInteractionJSONFromNode({
      node: el,
      Decoder: DecodeJSON
    }) as Record<string, unknown> | null | undefined

    if (!decoded || typeof decoded !== 'object') return

    const imageVal = decoded.image
    if (typeof imageVal !== 'string' || !imageVal.trim()) return

    slots.push({
      interactionElement: el as HTMLElement,
      jsonDiv,
      decoded,
      prompt: imageVal.trim()
    })
  })

  return { rootDocument, slots }
}

export function writeEncodedInteractionJson(
  jsonDiv: HTMLElement,
  decoded: Record<string, unknown>
): void {
  const clone = JSON.parse(JSON.stringify(decoded)) as Record<string, unknown>
  EncodeJSON(clone)
  jsonDiv.textContent = JSON.stringify(clone)
}

export function serializeMergedHtmlDocument(doc: Document): string {
  return doc.body ? doc.body.innerHTML : ''
}
